import React, { useEffect, useRef, useState } from "react";
import logger from "@/utils/logger";
import { ChevronLeft, ChevronRight, Send } from "lucide-react";
import MicrophoneIcon from "@/components/icons/microphone-icon";
import { Spinner } from "@/components/ui/spinner";

// Array of predefined chat prompts
const PREDEFINED_PROMPTS = [
  "Who has run this distance before?",
  "Which horse has the best jockey?",
  "Which horse has the best form?",
  "Who is the favorite?",
  "What's the track condition today?",
  "Which horse will do best in current track conditions?",
  "Any outsiders to consider?",
  "Tell me about the horse's recent performances",
  "Which trainer has the best record here?",
  // "What's the betting strategy for this race?",
];

export default function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  isDisabled = false,
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecordingPermission, setHasRecordingPermission] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [visiblePromptCount, setVisiblePromptCount] = useState(0);
  const [usedPrompts, setUsedPrompts] = useState(new Set());
  const recognitionRef = useRef(null);
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const cascadeTimeoutRef = useRef(null);
  const lastSpeechRef = useRef(Date.now());
  const finalTranscriptRef = useRef("");

  const clearPromptTimers = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (cascadeTimeoutRef.current) {
      clearTimeout(cascadeTimeoutRef.current);
    }
  };

  // Determines whether to show microphone or send button
  const showMicrophone = !isFocused && input.trim() === "";
  const showSendButton = isFocused || input.trim() !== "";

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    if (isDisabled) {
      setShowPrompts(false);
      setVisiblePromptCount(0);
      clearPromptTimers();
      return;
    }

    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Hide prompts and reset cascade when there's activity
    setShowPrompts(false);
    setVisiblePromptCount(0);

    // Start inactivity timer
    inactivityTimeoutRef.current = setTimeout(() => {
      setShowPrompts(true);
    }, 10000); // 10 seconds
  };

  // Setup cascade effect when prompts should be shown
  useEffect(() => {
    if (isDisabled) {
      setShowPrompts(false);
      setVisiblePromptCount(0);
      clearPromptTimers();
      return;
    }

    if (showPrompts) {
      // Clear any existing cascade
      if (cascadeTimeoutRef.current) {
        clearTimeout(cascadeTimeoutRef.current);
      }

      setVisiblePromptCount(0);

      // Start cascade effect
      const startCascade = () => {
        let count = 0;

        const addPrompt = () => {
          count++;
          setVisiblePromptCount(count);

          const availablePrompts = PREDEFINED_PROMPTS.filter(
            (prompt) => !usedPrompts.has(prompt),
          );

          if (count < availablePrompts.length) {
            cascadeTimeoutRef.current = setTimeout(addPrompt, 150); // Show next prompt after 150ms
          }
        };

        addPrompt();
      };

      startCascade();
    }
  }, [showPrompts, usedPrompts]);

  // Start inactivity timer on mount
  useEffect(() => {
    resetInactivityTimer();

    return () => {
      clearPromptTimers();
    };
  }, [isDisabled]);

  // Reset inactivity timer on user interaction
  useEffect(() => {
    if (isDisabled) return;
    resetInactivityTimer();
  }, [input, isFocused, isDisabled]);

  // Handle predefined prompt click
  const handlePromptClick = (prompt) => {
    if (isDisabled) return;

    setInput(prompt);
    // Add the prompt to used prompts
    setUsedPrompts((prev) => new Set([...prev, prompt]));
    // Reset inactivity timer
    resetInactivityTimer();

    // Submit the form with the selected prompt
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true }),
        );
      }
    }, 100);
  };

  const resetSilenceTimeout = () => {
    logger.log("Speech detected, resetting silence timer");
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }

    silenceTimeoutRef.current = setTimeout(() => {
      const silenceDuration = Date.now() - lastSpeechRef.current;
      logger.log("Checking silence duration:", silenceDuration);

      if (isRecording && silenceDuration > 1500) {
        logger.log("Silence detected for more than 1.5s, auto-submitting");
        if (finalTranscriptRef.current.trim()) {
          setInput(finalTranscriptRef.current);
          stopRecording();
        }
      }
    }, 1500);
  };

  const startRecording = async () => {
    if (isDisabled) return;

    try {
      if (!("webkitSpeechRecognition" in window)) {
        alert(
          "Speech recognition is not supported in this browser. Please use Chrome.",
        );
        return;
      }

      await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasRecordingPermission(true);
      logger.log("Starting recording...");

      recognitionRef.current = new webkitSpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      finalTranscriptRef.current = "";

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const isFinal = event.results[0].isFinal;

        if (isFinal) {
          finalTranscriptRef.current = transcript;
        }

        logger.log(
          "Speech detected:",
          transcript,
          isFinal ? "(final)" : "(interim)",
        );
        setInput(transcript);
        lastSpeechRef.current = Date.now();
        resetSilenceTimeout();
        resetInactivityTimer();
      };

      recognitionRef.current.onerror = (event) => {
        logger.error("Speech recognition error:", event.error);
        stopRecording();
      };

      recognitionRef.current.onend = () => {
        logger.log("Recognition ended, isRecording:", isRecording);
        // If we're still supposed to be recording, restart it
        if (isRecording) {
          logger.log("Restarting recognition...");
          recognitionRef.current.start();
        }
      };

      recognitionRef.current.onspeechend = () => {
        logger.log("Speech ended, starting silence detection");
        resetSilenceTimeout();
      };

      recognitionRef.current.start();
      setIsRecording(true);
      lastSpeechRef.current = Date.now();
      resetInactivityTimer();
    } catch (err) {
      logger.error("Error accessing microphone:", err);
      setHasRecordingPermission(false);
    }
  };

  const stopRecording = () => {
    logger.log("Stopping recording...");
    if (recognitionRef.current) {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      recognitionRef.current.stop();
      setIsRecording(false);

      // Submit the form after a short delay to ensure the final transcript is set
      setTimeout(() => {
        if (input.trim() && formRef.current) {
          logger.log("Submitting transcribed text:", input);
          formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true }),
          );
        }
      }, 100);
      resetInactivityTimer();
    }
  };

  const toggleRecording = () => {
    if (isDisabled) return;

    if (isRecording) {
      stopRecording();
    } else {
      setInput(""); // Clear existing input when starting new recording
      startRecording();
    }
    resetInactivityTimer();
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (isDisabled) return;

    if (isRecording) {
      stopRecording();
    }
    handleSubmit(e);
    // After submit, reset the UI state
    setIsFocused(false);
    resetInactivityTimer();
  };

  // Handle focus and blur events
  const handleFocus = () => {
    setIsFocused(true);
    resetInactivityTimer();
  };

  const handleBlur = () => {
    // Keep focused state if there's text in the input
    if (input.trim() === "") {
      setIsFocused(false);
    }
    resetInactivityTimer();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (cascadeTimeoutRef.current) {
        clearTimeout(cascadeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        className={`lexi-chat-input-container fixed bottom-[60px] left-[50%] translate-x-[-50%] max-w-md w-full mx-auto flex items-center gap-2 px-2 py-4 bg-white border-t border-gray-200
        ${showMicrophone ? "status-microphone-visible" : ""}
        ${showSendButton ? "status-submit-visible" : ""}
        ${isFocused ? "status-focused" : ""}
        ${input.trim() !== "" ? "status-has-content" : ""}
        ${isRecording ? "status-recording" : ""}
        ${isLoading ? "status-loading" : ""}
        ${isDisabled ? "opacity-60" : ""}`}
      >
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className={`nav-button nav-button-prev p-2 rounded-full hover:bg-gray-100 ${!canGoPrevious ? "opacity-50 cursor-not-allowed" : ""}`}
          data-gtm-element-id="todays-tips_chat_button-1"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        <form
          id="my-tips_chat_form-1"
          ref={formRef}
          onSubmit={onFormSubmit}
          className={`relative flex-1 flex items-center gap-2
            ${showMicrophone ? "status-microphone-visible" : ""}
            ${showSendButton ? "status-submit-visible" : ""}
            ${isFocused ? "status-focused" : ""}
            ${input.trim() === "" ? "input-empty" : "input-filled"}
            ${isLoading ? "is-loading" : "not-loading"}
            ${isRecording ? "is-recording" : "not-recording"}`}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            //
            placeholder={isDisabled ? "Upgrade for LexiChat..." : "Ask Lexi about this race..."}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            className={`flex-1 text-sm pl-4 py-2 rounded-full border border-gray-300 bg-white outline-none
              ${showMicrophone ? "pr-[4.5em] status-microphone-visible" : ""}
              ${showSendButton ? "pr-[4.5em] status-submit-visible" : ""}
              ${isFocused ? "status-focused" : ""}
              ${input.trim() !== "" ? "status-has-content" : ""}`}
            data-gtm-element-id="todays-tips_chat_input-1"
          />

          {/* Microphone button - only visible when not focused and input is empty */}
          {showMicrophone && (
            <button
              type="button"
              onClick={toggleRecording}
              disabled={hasRecordingPermission === false || isDisabled}
              className={`lexi-chat-microphone-button absolute right-[33px] top-0 p-2 rounded-full transition-colors duration-200
                ${isRecording ? "is-recording bg-[#F30099] text-white" : "not-recording hover:bg-[#F30099] hover:text-white"}
                ${hasRecordingPermission === false || isDisabled ? "no-permission opacity-50 cursor-not-allowed" : "has-permission"}`}
              data-gtm-element-id="todays-tips_microphone_button-1"
            >
              <MicrophoneIcon
                className={`w-5 h-5 ${isRecording ? "text-white" : "text-gray-400 hover:text-white"}`}
              />
            </button>
          )}

          {/* Submit button - only visible when focused or input has text */}
          {showSendButton && (
            <button
              type="submit"
              disabled={isLoading || isDisabled}
              className={`lexi-chat-submit-button absolute right-0 top-0 p-2 rounded-full hover:bg-gray-100 transition-opacity duration-200 ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
              data-gtm-element-id="todays-tips_send_button-1"
            >
              {isLoading ? (
                <Spinner size="small" className="svg-spinner text-[#F30099]" />
              ) : (
                <Send className="svg-send w-5 h-5 text-[#F30099]" />
              )}
            </button>
          )}
        </form>

        <button
          onClick={onNext}
          disabled={!canGoNext}
          className={`nav-button nav-button-next p-2 rounded-full hover:bg-gray-100 ${!canGoNext ? "opacity-50 cursor-not-allowed" : ""}`}
          data-gtm-element-id="todays-tips_chat_button-4"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Predefined prompts section */}
      {showPrompts && !isDisabled && (
        <div className="fixed bottom-[140px] left-[50%] translate-x-[-50%] max-w-md w-full mx-auto px-0">
          <div className="flex overflow-x-auto pb-0 hide-scrollbar">
            <div className="flex gap-2 px-1">
              {PREDEFINED_PROMPTS.filter((prompt) => !usedPrompts.has(prompt))
                .slice(0, visiblePromptCount)
                .map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt)}
                    className="text-sm px-3 py-3 rounded-t-xl rounded-r-xl border-2 border-pink-300 bg-white hover:bg-gray-50 text-gray-700 whitespace-nowrap flex-shrink-0 animate-fadeIn"
                    data-gtm-element-id={`predefined-prompt-${index}`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {prompt}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
